<template>
    <Preloader v-if="isLoading" />
    <LeftSidebar v-if="shouldShowSidebar && !isNotFound" />
    <div
        :class="[
            'main-content d-flex flex-column',
            { active: stateStoreInstance.open },
            { 'padding-minus': shouldShowPaddingLeftZero || isNotFound },
            { 'padding-zero': isLayoutHiddenNotPublicPages },
            { 'auth-padding-zero': shouldRemoveAuthHorizontalPadding },
        ]"
    >
        <TopHeader v-if="shouldShowHeader && !isNotFound" />
        <div>
            <router-view />
        </div>
        <div class="flex-grow-1"></div>
        <MainFooter v-if="shouldShowFooter && !isNotFound" />
        <SnackbarMessage
            v-model:visible="snackbarVisible"
            :message="snackbarMessage"
            :type="snackbarType"
            :icon="snackbarIcon"
        />
    </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watchEffect } from "vue";
import { useRoute } from "vue-router";
import stateStore from "../utils/store";
import Preloader from "../components/Layouts/Preloader.vue";
import LeftSidebar from "../components/Layouts/LeftSidebar.vue";
import TopHeader from "../components/Layouts/TopHeader/TopHeader.vue";
import MainFooter from "../components/Layouts/MainFooter.vue";
import SnackbarMessage from "@/components/common/SnackbarMessage.vue";
import messageService from "@/services/messages.js";

export default defineComponent({
    name: "App",
    components: {
        Preloader,
        LeftSidebar,
        TopHeader,
        MainFooter,
        SnackbarMessage,
    },

    setup() {
        const route = useRoute();
        const isLoading = ref(true);
        const stateStoreInstance = stateStore;
        const snackbarVisible = ref(false);
        const snackbarMessage = ref("");
        const snackbarType = ref("info");
        const snackbarIcon = ref("");
        const routePath = computed(() => route.path ?? "");

        // O accounts so tem dois layouts: a tela inteira sem moldura (hoje so a
        // /sso, que dura um piscar) e o resto, que e o modo logado com sidebar,
        // header e rodape. Quem escolhe e o `meta.layout` da rota, nao uma
        // lista de caminhos -- lista de caminhos e o que o admin faz, e la ela
        // ja esta desencontrada das rotas reais.
        const isLayoutHidden = computed(() => route.meta?.layout === "blank");
        const isLayoutHiddenNotPublicPages = isLayoutHidden;
        const shouldShowSidebar = computed(() => !isLayoutHidden.value);
        const shouldShowHeader = computed(() => !isLayoutHidden.value);
        const shouldShowFooter = computed(() => !isLayoutHidden.value);
        const shouldShowPaddingLeftZero = computed(() => isLayoutHidden.value);
        const shouldRemoveAuthHorizontalPadding = isLayoutHidden;

        const isNotFound = computed(() => {
            return route.matched.some(
                (record) => record.path === "/:pathMatch(.*)*",
            );
        });

        // Global message handler
        messageService.setCallback((type, msg, icon) => {
            snackbarType.value = type;
            snackbarMessage.value = msg;
            snackbarIcon.value = icon;
            snackbarVisible.value = true;
        });

        onMounted(() => {
            setTimeout(() => {
                isLoading.value = false;
            }, 1000);

            watchEffect(() => {
                if (stateStoreInstance.open) {
                    document.body.classList.remove("sidebar-show");
                    document.body.classList.add("sidebar-hide");
                } else {
                    document.body.classList.remove("sidebar-hide");
                    document.body.classList.add("sidebar-show");
                }
            });
        });

        return {
            isLoading,
            stateStoreInstance,
            route,
            routePath,
            snackbarVisible,
            snackbarMessage,
            snackbarType,
            snackbarIcon,
            shouldShowSidebar,
            shouldShowHeader,
            shouldShowFooter,
            shouldShowPaddingLeftZero,
            shouldRemoveAuthHorizontalPadding,
            isNotFound,
            isLayoutHiddenNotPublicPages,
        };
    },
});
</script>

<style lang="scss" scoped>
.main-content {
    overflow: hidden;
    min-height: 100vh;
    transition: var(--transition);
    padding: {
        top: 83px;
        left: 285px;
        right: 25px;
    }
    &.active {
        padding-left: 85px;
    }
    &.right-sidebar {
        padding: {
            left: 25px;
            right: 285px;
        }
        &.active {
            padding-right: 85px;
        }
    }
    &.hide-sidebar {
        padding-left: 85px;

        &.right-sidebar {
            padding: {
                left: 25px;
                right: 85px;
            }
        }
    }
    &.padding-minus {
        padding-left: 25px;
        padding-top: 0;
    }
    &.padding-zero {
        padding-left: 0 !important;
        padding-right: 0 !important;
        padding-top: 0 !important;
    }
    &.auth-padding-zero {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }
}

@media only screen and (max-width: 767px) {
    .main-content {
        padding: {
            top: 0;
            left: 15px !important;
            right: 15px;
        }
        &.active {
            padding-left: 15px;
        }
    }
}

@media only screen and (min-width: 768px) and (max-width: 991px) {
    .main-content {
        padding: {
            top: 0;
            left: 25px;
            right: 25px;
        }
        &.active {
            padding-left: 25px;
        }
    }
}

@media only screen and (min-width: 992px) and (max-width: 1199px) {
    .main-content {
        padding: {
            top: 0;
            left: 25px;
            right: 25px;
        }
        &.active {
            padding-left: 25px;
        }
    }
}
</style>
